import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import isEqual from 'lodash-es/isEqual';

import { FaFilter, FaUndo } from 'react-icons/fa';
import { Dropdown } from 'react-bootstrap';

import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import RelationSearch from '@/shared/components/input/RelationSearch';
import userStorage from '@/shared/services/userStorage';
import guid from '@/shared/utils/guid';
import CheckboxDropdownMenu from '@/shared/components/input/CheckboxDropdownMenu';
import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  initDynamicFilters,
} from '@/shared/components/DynamicFilter';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import UserSearch from '@/shared/components/input/UserSearch';
import messages from './messages';

export const defaultFilters = {
  relationId: null, // String
  relationName: null,
  assistant: null, // UserId
  responsible: null, // UserId
  associate: null, // UserId
  assistantBackup: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  relationTypes: [], // Array
  legalForms: [], // Array
  withLabels: [], // Array
  withoutLabels: [], // Array,
  ownCompanies: [],
  assignmentTemplateId: null,
  withCategoryId: null,
  withoutCategoryId: null,
  endFiscalYears: [],
  financialStatementSchemes: [],
  accountingSoftware: [],
  otherSoftware: [],
  invoiceScheduleTypes: [],
  userId: null,
  filterBehaviours: {
    withLabels: 1,
  },
  relationGroups: [],
};

const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(defaultFilters, 'userId'),
  { id: 'userId', label: messages.User },
];

const dynamicFiltersStorageKey = 'PlannedRegistrationOverviewDynamicFilters';

class PlannedRegistrationOverviewFilters extends Component {
  state = {
    labels: [],
    withCategories: [],
    withoutCategories: [],
    dynamicFilters: [],
  };

  componentDidMount() {
    const { intl, filters } = this.props;

    Promise.all([
      api.base.get(`${config.appUrls.api}/api/Resource/Tags`),
      api.base.get(`${config.appUrls.api}/api/Resource/Categories/`),
    ]).then(([{ data: tags }, { data: categoriesResult }]) => {
      const categories = categoriesResult
        .map(x => {
          const codePrefix = x.code ? `${x.code} – ` : '';

          const label = `${codePrefix}${intl.formatMessage(
            listItemMsg('resourceCategory', x.id, x.name),
          )}`;
          return { key: x.id, label, type: x.type };
        })
        .sort(dynamicSortMultiple('type', 'label'));

      this.setState({
        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(dynamicFiltersStorageKey) || [],
          filters,
          defaultFilters,
        ),
        withCategories: [
          { key: guid.empty, label: intl.formatMessage(messages.WithPlannedRegistrations) },
          ...categories,
        ],
        withoutCategories: [
          { key: guid.empty, label: intl.formatMessage(messages.WithoutPlannedRegistrations) },
          ...categories,
        ],
        labels: tags
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
          .sort(dynamicSort('sort')),
      });
    });

    api.base.get(`${config.appUrls.api}/api/Resource/Categories/`).then(({ data }) => {
      const categories = data
        .map(x => {
          const codePrefix = x.code ? `${x.code} – ` : '';

          const label = `${codePrefix}${intl.formatMessage(
            listItemMsg('resourceCategory', x.id, x.name),
          )}`;
          return { key: x.id, label, type: x.type };
        })
        .sort(dynamicSortMultiple('type', 'label'));
      this.setState({
        withCategories: [
          { key: guid.empty, label: intl.formatMessage(messages.WithPlannedRegistrations) },
          ...categories,
        ],
        withoutCategories: [
          { key: guid.empty, label: intl.formatMessage(messages.WithoutPlannedRegistrations) },
          ...categories,
        ],
      });
      this.setState({
        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(dynamicFiltersStorageKey) || [],
          filters,
          defaultFilters,
        ),
      });
    });
  }

  filterChanged = filter => {
    const { onFilterChange, filters } = this.props;
    const newFilters = { ...filters, ...filter };

    if (!isEqual(filters, newFilters)) {
      onFilterChange(newFilters);
    }
  };
  dynamicFilterChanged = (item, selected) => {
    const { dynamicFilters } = this.state;
    const newFilters = dynamicFilters.map(x => {
      if (x.id !== item.id && item.id !== 'selectall') return x;
      return { ...x, selected };
    });

    userStorage.setItem(
      dynamicFiltersStorageKey,
      newFilters.filter(x => x.selected).map(x => x.id),
    );
    this.setState({ dynamicFilters: newFilters });

    const { filters } = this.props;
    if (!selected && !isEqual(filters[item.id], defaultFilters[item.id])) {
      // Reset filter when it's removed
      this.filterChanged({ [item.id]: defaultFilters[item.id] });
    }
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'userId': {
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <UserSearch
                withSystemUser
                onChange={user => this.filterChanged({ userId: user?.id ?? null })}
                value={filters.userId}
                placeholder={intl.formatMessage(messages.User)}
                disabled={!!filters.withoutCategoryId}
              />
            </div>
          </div>
        );
      }
      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };
  render() {
    const { filters, intl, countSelected } = this.props;
    const { dynamicFilters, withCategories, withoutCategories, labels } = this.state;
    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;

    const withCategory =
      withCategories && filters.withCategoryId
        ? withCategories.find(x => x.key === filters.withCategoryId)
        : null;
    const withoutCategory =
      withoutCategories && filters.withoutCategoryId
        ? withoutCategories.find(x => x.key === filters.withoutCategoryId)
        : null;

    const hasFilters = !isEqual(filters, defaultFilters);
    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);
    const dropdownFilters = [
      {
        id: 'selectall',
        label: intl.formatMessage(messages.SelectAll),
        selected: !dynamicFilters.some(x => !x.selected),
      },
      ...dynamicFilters,
    ];
    return (
      <div>
        <div className="row">
          <div className="col-lg-4">
            <div className="form-group">
              <RelationSearch
                onChange={item => {
                  if (!item || !item.id) {
                    this.filterChanged({ relationId: null, relationName: null });
                  } else {
                    this.filterChanged({
                      relationId: item.key,
                      relationName: item.label,
                    });
                  }
                }}
                initialSelectedItem={initRelation}
              />
            </div>
          </div>
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <AutoComplete
                onChange={e =>
                  this.filterChanged({
                    withCategoryId: e && e.key ? e.key : null,
                    withoutCategoryId: null,
                  })
                }
                onClear={() => this.filterChanged({ withCategoryId: null })}
                value={withCategory}
                items={withCategories}
                placeholder={intl.formatMessage(messages.WithPriceListItem)}
                mode="edit"
              />
            </div>
          </div>
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <AutoComplete
                onChange={e =>
                  this.filterChanged({
                    withoutCategoryId: e && e.key ? e.key : null,
                    withCategoryId: null,
                    userId: null,
                  })
                }
                onClear={() => this.filterChanged({ withoutCategoryId: null })}
                value={withoutCategory}
                items={withoutCategories}
                placeholder={intl.formatMessage(messages.WithoutPriceListItem)}
                mode="edit"
              />
            </div>
          </div>
        </div>
        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row">
            {visibleFilters.map(id => (
              <DynamicFilter
                key={id}
                type={id}
                intl={intl}
                labels={labels}
                filters={filters}
                filterChanged={this.filterChanged}
                renderCustomFilter={this.renderDynamicFilter}
              />
            ))}
          </div>
        ) : null}
        <div className="row">
          <div className="col-sm-12">
            <Dropdown id="dynamicFilterMenu">
              <Dropdown.Toggle title={intl.formatMessage(messages.SelectYourFilters)}>
                <FaFilter className="react-icon margin5R" />
                <FormattedMessage {...messages.Filters} />
              </Dropdown.Toggle>
              <CheckboxDropdownMenu
                bsRole="menu"
                items={dropdownFilters}
                onItemChanged={this.dynamicFilterChanged}
              />
            </Dropdown>
            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset margin10L"
                onClick={() => this.filterChanged(defaultFilters)}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
            {countSelected && <div className="pull-right margin10T">{countSelected}</div>}
          </div>
        </div>
      </div>
    );
  }
}

PlannedRegistrationOverviewFilters.propTypes = {
  filters: PropTypes.shape({
    relationId: PropTypes.string,
    relationName: PropTypes.string,
    responsible: PropTypes.string,
    assistant: PropTypes.string,
    associate: PropTypes.string,
    assistantBackup: PropTypes.string,
    billingUser: PropTypes.string,
    seniorUser: PropTypes.string,
    relationType: PropTypes.arrayOf(PropTypes.number),
    legalForms: PropTypes.arrayOf(PropTypes.number),
    withLabels: PropTypes.arrayOf(PropTypes.string),
    withoutLabels: PropTypes.arrayOf(PropTypes.string),
    ownCompanies: PropTypes.arrayOf(PropTypes.string),
    relationGroups: PropTypes.arrayOf(PropTypes.string),
    assignmentTemplateId: PropTypes.string,
    withCategoryId: PropTypes.string,
    withoutCategoryId: PropTypes.string,
    accountingSoftware: PropTypes.arrayOf(PropTypes.number),
    otherSoftware: PropTypes.string,
    userId: PropTypes.string,
  }),
  onFilterChange: PropTypes.func.isRequired,
  countSelected: PropTypes.string,
  intl: intlShape.isRequired,
};
PlannedRegistrationOverviewFilters.defaultProps = {
  filters: defaultFilters,
  countSelected: '',
};

export default injectIntl(PlannedRegistrationOverviewFilters);
